"use client";
import { useContext, useEffect, useState } from "react";
import moment from "moment";
import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import Select from "react-select";
import AsyncSelect from "react-select/async";

import { GetMedicalHistory } from "@/actions/templates/MedicalHistory/GetMedicalHistory";
import { GetMedicationDetails } from "@/actions/templates/MedicationDetails/GetMedicationDetails";
import { Link } from "@chakra-ui/next-js";

import HistorySection from "@/components/dashboard/history/HistorySection";
import HistoryRecordsTable from "@/components/dashboard/HistoryRecordsTable";
import {
  MedicationDetailsPayload,
  PostMedicationDetails,
} from "@/actions/templates/MedicationDetails/PostMedicationDetails";
import { Controller, useForm } from "react-hook-form";
import {
  EVENT_LIBRARY,
  HISTORY_TEMPLATES,
  MEDICATIONS_API_URL,
  REQUIRED_FIELD,
  ROUTE_PATHS,
  SERVER_DATE_FORMAT,
} from "@/lib/constants";
import { PutMedicationDetails } from "@/actions/templates/MedicationDetails/PutMedicationDetails";
import { DeleteMedicationDetails } from "@/actions/templates/MedicationDetails/DeleteMedicationDetails";
import HistoryModal from "@/components/dashboard/history/HistoryModal";
import FormRow from "@/components/dashboard/application/FormRow";
import { AuthContext } from "@/providers/auth";
import { GetSearchMedications } from "@/actions/common/GetSearchMedications";
import { PostMedicationNames } from "@/actions/common/PostMedicationNames";
import useAnalytics from "@/lib/hooks/useAnalytics";
import handleServerAction from "@/lib/hooks/handleServerAction";
import { useIsTextMasked } from "../children/useIsDataMasked";
import {
  InputWrapper,
  maskValue,
} from "@/components/forms/JailTextInputWrapper";
import { IoWarningOutline } from "react-icons/io5";
import axios from "axios";
import { PostMedicalHistory } from "@/actions/templates/MedicalHistory/PostMedicalHistory";

const SEARCH_DELAY = 500;

const MissingField = () => (
  <Flex alignItems="center" gap="6px" color="gray.400">
    <Icon as={IoWarningOutline} /> Missing
  </Flex>
);

const category = "Medication";
const defaultValues = HISTORY_TEMPLATES[category].defaultValues;
const OTHER_PHYSICIANS = [
  "Other Doctor/Healthcare Provider",
  "Other Hospital/Clinic",
];

const MedicationHistoryPage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { getUserDetails } = useContext(AuthContext);

  const toast = useToast();
  const track = useAnalytics();
  const isTextMasked = useIsTextMasked();

  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);
  const [physicianOptions, setPhysicianOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const getPhysicians = async () => {
    const data = await handleServerAction(GetMedicalHistory);
    setPhysicianOptions(
      data
        .map((physician: any) => ({
          label: isTextMasked
            ? maskValue(physician.physicianName)
            : physician.physicianName,
          value: physician.id,
        }))
        .concat(
          OTHER_PHYSICIANS.filter(
            (val) =>
              !data.map((record: any) => record.physicianName).includes(val)
          ).map((val) => ({
            label: val,
            value: val,
          }))
        )
    );
  };

  const {
    handleSubmit,
    register,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<MedicationDetailsPayload>({
    defaultValues,
  });

  const refreshState = async () => {
    onClose();
    await getRecords();
    await getUserDetails();
    reset(defaultValues);
  };

  const FindOrAddMedication = async (medicationName: string) => {
    const medications =
      (await handleServerAction(GetSearchMedications, {
        keyword: medicationName,
      })) || [];
    let medication = medications.filter(
      (item: { name: string }) =>
        item.name.trim().toLowerCase() === medicationName.trim().toLowerCase()
    )[0];
    if (!medication) {
      medication = await handleServerAction(PostMedicationNames, {
        name: medicationName,
        approved: false,
      });
    }
    return medication;
  };

  const onAdd = async (payload: MedicationDetailsPayload) => {
    try {
      const medication = await FindOrAddMedication(
        payload.MedicationName || ""
      );

      await handleServerAction(PostMedicationDetails, {
        ...payload,
        MedicationId: medication.id,
        MedicationName: medication.name,
        PrescriptionStartDate: moment(payload.PrescriptionStartDate).format(
          SERVER_DATE_FORMAT
        ),
      });
      await refreshState();
      toast({
        status: "success",
        title: "Record Added",
        description: "Record has been successfully added.",
      });

      track(EVENT_LIBRARY.ADDED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onUpdate = async (payload: MedicationDetailsPayload) => {
    try {
      const medication = await FindOrAddMedication(
        payload.MedicationName || ""
      );
      await PutMedicationDetails({
        ...payload,
        MedicationId: medication.id,
        PrescriptionStartDate: moment(payload.PrescriptionStartDate).format(
          SERVER_DATE_FORMAT
        ),
      });
      await refreshState();

      toast({
        status: "success",
        title: "Record Updated",
        description: "Record has been successfully updated.",
      });

      track(EVENT_LIBRARY.UPDATED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onEdit = (id: number) => {
    const editRecord = records.find((record) => record.id === id);
    if (editRecord) {
      reset({
        Id: editRecord?.id,
        PhysicianId: editRecord?.physicianId,
        MedicationName: editRecord?.prescriptionName,
        MedicationId: editRecord?.medicationId,
        PrescriptionDetails: editRecord?.prescriptionDetails,
        PrescriptionStartDate: editRecord?.prescriptionStartDate
          ? moment(editRecord?.prescriptionStartDate).format("YYYY-MM-DD")
          : "",
      });
      onOpen();
    }
  };

  const getRecords = async () => {
    setIsLoading(true);
    const data = await handleServerAction(GetMedicationDetails);
    setRecords(data);
    setIsLoading(false);
  };

  useEffect(() => {
    getPhysicians();
    getRecords();
  }, []);

  const onClick = () => {
    reset(defaultValues);
    onOpen();
  };

  const Id = watch("Id");

  const loadOptions = (
    inputValue: string,
    callback: (
      // eslint-disable-next-line no-unused-vars
      options: {
        label: string;
        value: string;
      }[]
    ) => void
  ) => {
    setTimeout(async () => {
      const { data } = await axios.get(`${MEDICATIONS_API_URL}${inputValue}`);
      callback(
        data[3].map((result: string[]) => ({
          label: result[0],
          value: result[0],
        }))
      );
    }, SEARCH_DELAY);
  };

  const physicianOnChange = async (val: any, field: any) => {
    if (
      OTHER_PHYSICIANS.includes(val.value) &&
      physicianOptions.find((option) => option.value === val.value)
    ) {
      const newPhysician = await handleServerAction(PostMedicalHistory, {
        PhysicianName: val.value,
      });
      await getPhysicians();
      // update physician options
      field.onChange(newPhysician.id);
    } else {
      field.onChange(val?.value);
    }
  };

  return (
    <HistorySection category={category} isLoading={isLoading} onClick={onClick}>
      <HistoryRecordsTable
        category={category}
        records={records}
        cells={records.map((record) => [
          record.prescriptionName,
          record.physicianName || <MissingField />,
          record.prescriptionDetails || <MissingField />,
          record.prescriptionStartDate
            ? moment(record.prescriptionStartDate).format("M/D/YYYY")
            : "",
        ])}
        onEdit={onEdit}
        deleteAction={DeleteMedicationDetails}
        getRecords={getRecords}
      />
      <HistoryModal
        category={category}
        mode={Id ? "Update" : "Add"}
        isOpen={isOpen}
        onClose={onClose}
        onAdd={handleSubmit(onAdd)}
        onUpdate={handleSubmit(onUpdate)}
        isSubmitting={isSubmitting}
      >
        <FormRow
          label="Physician that prescribed the medication"
          size="sm"
          fieldError={errors.PhysicianId}
        >
          <Controller
            name="PhysicianId"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            render={({ field }) => (
              <Select
                value={
                  field.value
                    ? physicianOptions.find(
                        (option) => option.value === field.value
                      )
                    : undefined
                }
                onChange={(val) => physicianOnChange(val, field)}
                placeholder="Select Doctor"
                options={physicianOptions}
                styles={{
                  container: (styles) => ({
                    ...styles,
                    width: "100%",
                  }),
                }}
              />
            )}
          />
        </FormRow>
        {physicianOptions.length === 2 && (
          <Alert status="info" borderRadius="8px" mb="20px">
            <AlertIcon />
            <HStack w="100%" justifyContent="space-between" alignItems="center">
              <Text>Add physicians to your Doctor Visits history.</Text>
              <Button
                as={Link}
                href="/dashboard/history/doctors"
                variant="solid"
              >
                Visit
              </Button>
            </HStack>
          </Alert>
        )}
        <FormRow
          label="Medication/Prescription Name"
          size="sm"
          fieldError={errors.MedicationName}
        >
          <Controller
            name="MedicationName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            render={({ field }) => (
              <AsyncSelect
                value={
                  field.value
                    ? {
                        label: field.value,
                        value: field.value,
                      }
                    : undefined
                }
                onChange={(val) => {
                  field.onChange(val?.value);
                }}
                placeholder="Search Medications..."
                loadOptions={loadOptions}
                styles={{
                  container: (styles) => ({
                    ...styles,
                    width: "100%",
                  }),
                }}
              />
            )}
          />
        </FormRow>
        <FormRow
          label="Medication Details"
          size="sm"
          fieldError={errors.PrescriptionDetails}
        >
          <InputWrapper
            name="PrescriptionDetails"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder="What is it for?"
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label="Prescription Start Date"
          size="sm"
          fieldError={errors.PrescriptionStartDate}
        >
          <Input
            type="date"
            textTransform="uppercase"
            {...register("PrescriptionStartDate", { required: REQUIRED_FIELD })}
          />
        </FormRow>
      </HistoryModal>
    </HistorySection>
  );
};

export default MedicationHistoryPage;
